import { QueryCache, type Query } from '@tanstack/react-query';
import { type NonJsonErrorContext } from '@/utils/reportNonJsonError';
import { reportOnFinalFailure } from '@/utils/withJsonGuard';

/**
 * Query `meta` that opts a guarded read into report-once: its `queryFn` runs
 * `withJsonGuard(fn, ctx, { report: false })`, and the cache reports the failure
 * a single time, after React-Query's retries are exhausted. A retry that recovers
 * reports nothing.
 *
 * Reporting from the cache rather than from `useQuery`'s `onError` matters: that
 * callback fires once per mounted observer, and `useMenu` alone has nine call sites.
 * (TEAM-15682)
 */
export const guardedReadMeta = (ctx: NonJsonErrorContext) => ({ guardedRead: ctx });

export function reportGuardedQueryFailure(
  error: unknown,
  query: Query<unknown, unknown, unknown>,
): void {
  const ctx = query.meta?.guardedRead as NonJsonErrorContext | undefined;
  if (!ctx) return;
  // `fetchFailureCount` already includes this final failure, so it is the attempt count.
  reportOnFinalFailure(ctx)(error, query.state.fetchFailureCount);
}

export const createQueryCache = () => new QueryCache({ onError: reportGuardedQueryFailure });
